import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

type ViewModalProps<T> = {
  open: boolean;
  onClose: () => void;
  title: string;
  data: T;
  columns?: {
    label?: string;
    key: keyof T;
    type?: "html" | "text" | "number" | "date";
    render?: (value: any) => React.ReactNode;
  }[];
};

function ViewModal<T>({
  open,
  onClose,
  title,
  data,
  columns,
}: ViewModalProps<T>): JSX.Element {
  // If no columns are provided, try to render the data as is
  const renderValue = (key: keyof T, value: any) => {
    if (columns) {
      const column = columns.find((col) => col.key === key);
      if (column?.render) {
        return column.render(value);
      }
      if (column?.type === "html") {
        return (
          <Box
            dangerouslySetInnerHTML={{ __html: value }}
            sx={{
              maxHeight: "400px",
              overflowY: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              padding: "12px",
              backgroundColor: "#fafafa",
            }}
          />
        );
      }
    }

    // Default rendering
    if (typeof value === "string" && value.includes("<")) {
      // Assume it's HTML content
      return (
        <Box
          dangerouslySetInnerHTML={{ __html: value }}
          sx={{
            maxHeight: "400px",
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            borderRadius: "4px",
            padding: "12px",
            backgroundColor: "#fafafa",
          }}
        />
      );
    }

    return (
      <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
        {String(value)}
      </Typography>
    );
  };

  const renderData = () => {
    if (columns) {
      return columns.map((column) => {
        const value = data[column.key];
        return (
          <Box key={String(column.key)} sx={{ marginBottom: "16px" }}>
            {column.label && (
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ marginBottom: "4px" }}
              >
                {column.label}:
              </Typography>
            )}
            {renderValue(column.key, value)}
          </Box>
        );
      });
    }

    // If no columns provided, render all properties
    return Object.entries(data as Record<string, any>).map(([key, value]) => (
      <Box key={key} sx={{ marginBottom: "16px" }}>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ marginBottom: "4px" }}
        >
          {key}:
        </Typography>
        {renderValue(key as keyof T, value)}
      </Box>
    ));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: "8px",
            top: "8px",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ paddingTop: "16px" }}>{renderData()}</DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Закрыть
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ViewModal;
